import React from 'react'
import { CSSTransition } from 'react-transition-group'
import cx from 'classnames'

import { Backdrop } from 'components/shared/Backdrop/Backdrop'
import { TetherPortal } from 'components/shared/TetherPortal/TetherPortal'
import { Content } from './common/Content/Content'
import { Footer } from './common/Footer/Footer'
import { Header } from './common/Header/Header'

import { ModalHandlersContext, ModalPropsContext } from './context'
import type {
  ModalHandlersContextValue,
  ModalPropsContextValue,
} from './context'

import styles from './Modal.module.scss'

type ModalProps = {
  backLabel?: React.ReactNode
  cancelLabel?: React.ReactNode
  children?: React.ReactNode
  className?: string
  confirmIcon?: React.ComponentProps<
    typeof import('components/shared/Button/Button').Button
  >['icon']
  confirmLabel?: React.ReactNode
  isConfirmDisabled?: boolean
  isOpen: boolean
  nestedModalEscapeCapture?: boolean
  onBack?: () => void
  onCancel?: () => void
  onClose: () => void
  onConfirm?: () => void
  portalClassName?: string
  title?: React.ReactNode
}

type ModalComponent = ((props: ModalProps) => React.JSX.Element) & {
  Content: typeof Content
  Footer: typeof Footer
  Header: typeof Header
}

export const Modal: ModalComponent = Object.assign(
  ({
    children,
    portalClassName,
    className,
    backLabel,
    cancelLabel,
    confirmIcon,
    confirmLabel,
    title,
    onBack,
    onCancel,
    onClose,
    onConfirm,
    isConfirmDisabled,
    isOpen,
    nestedModalEscapeCapture = false,
  }: ModalProps) => {
    const ref = React.useRef<HTMLDivElement>(null)

    const propsContext = React.useMemo<ModalPropsContextValue>(
      () => ({
        backLabel,
        cancelLabel,
        confirmIcon,
        confirmLabel,
        title,
        isConfirmDisabled,
        isOpen,
      }),
      [
        backLabel,
        cancelLabel,
        confirmIcon,
        confirmLabel,
        title,
        isConfirmDisabled,
        isOpen,
      ],
    )

    const handlersContext = React.useMemo<ModalHandlersContextValue>(
      () => ({
        onBack,
        onCancel,
        onClose,
        onConfirm,
      }),
      [onBack, onCancel, onClose, onConfirm],
    )

    React.useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden'
      }

      return () => {
        document.body.style.overflow = 'initial'
      }
    }, [isOpen])

    React.useEffect(() => {
      if (!isOpen) return

      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' && event.key !== 'Esc') return

        if (nestedModalEscapeCapture) {
          event.preventDefault()
          event.stopImmediatePropagation()
        }

        onClose()
      }

      const options = nestedModalEscapeCapture ? { capture: true } : false

      document.addEventListener('keydown', handleEscKey, options)

      return () => {
        document.removeEventListener('keydown', handleEscKey, options)
      }
    }, [onClose, isOpen, nestedModalEscapeCapture])

    return (
      <ModalPropsContext.Provider value={propsContext}>
        <ModalHandlersContext.Provider value={handlersContext}>
          {/* TODO: resolve enter animation since it doesn't
        fire at all unlike exit animation */}
          <TetherPortal {...{ isOpen }}>
            <CSSTransition
              classNames={{
                enter: styles.enter,
                enterActive: styles.enterActive,
                exit: styles.exit,
                exitActive: styles.exitActive,
              }}
              nodeRef={ref}
              timeout={200}
              in={isOpen}
              appear
              mountOnEnter
              unmountOnExit
            >
              <div className={cx(styles.portal, portalClassName)}>
                <Backdrop isVisible={isOpen} />
                <div
                  aria-modal="true"
                  aria-labelledby={
                    typeof title === 'string' ? title : undefined
                  }
                  className={cx(styles.content, className)}
                  ref={ref}
                  role="dialog"
                >
                  {children}
                </div>
              </div>
            </CSSTransition>
          </TetherPortal>
        </ModalHandlersContext.Provider>
      </ModalPropsContext.Provider>
    )
  },
  {
    Content,
    Header,
    Footer,
  },
)
